<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { ref, computed, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import AtecSalesAPI from 'dashboard/api/atecSales';
import AtecPaymentMethodsAPI from 'dashboard/api/atecPaymentMethods';

const props = defineProps({
  cart: { type: Array, required: true },
  conversationId: { type: [String, Number], required: true },
  inboxId: { type: [String, Number], required: true },
  contactId: { type: [String, Number], required: true },
  allowProductDiscountPerItem: { type: Boolean, default: false },
  maxDiscountPercent: { type: Number, default: null },
});

const emit = defineEmits([
  'update-quantity',
  'update-item-price',
  'remove-from-cart',
  'clear-cart',
]);

const store = useStore();
const isProcessing = ref(false);
const isSendingQuote = ref(false);
const notes = ref('');
const discount = ref(0);
const paymentMethods = ref([]);
const selectedPaymentMethodId = ref(null);
const selectedPaymentMethodName = ref('');
const existingSales = ref([]);
const allowResubmit = ref(false);
const paymentDropdownOpen = ref(false);
const priceDropdownOpenId = ref(null);

const hasSentSale = computed(
  () => existingSales.value.length > 0 && !allowResubmit.value
);
const latestSale = computed(() => existingSales.value[0] || null);

const formatDate = isoString => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('pt-BR');
};

const subtotal = computed(() =>
  props.cart.reduce(
    (total, item) => total + (item.customPrice ?? item.price) * item.quantity,
    0
  )
);

const discountAmount = computed(() => (subtotal.value * discount.value) / 100);
const total = computed(() => subtotal.value - discountAmount.value);

const formatPrice = price =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    price
  );

const updateItemQuantity = (productId, newQuantity) =>
  emit('update-quantity', productId, newQuantity);

const removeItem = productId => emit('remove-from-cart', productId);
const clearCart = () => emit('clear-cart');

const closePriceDropdown = () => {
  priceDropdownOpenId.value = null;
};

const togglePriceDropdown = itemId => {
  priceDropdownOpenId.value =
    priceDropdownOpenId.value === itemId ? null : itemId;
};

const closePaymentDropdown = () => {
  paymentDropdownOpen.value = false;
};

const selectPaymentMethod = method => {
  selectedPaymentMethodId.value =
    method.Fpg_Codigo ?? method.codigo ?? method.id ?? null;
  selectedPaymentMethodName.value =
    method.Fpg_Descricao ?? method.descricao ?? method.nome ?? method.name ?? 'Pagamento';
  closePaymentDropdown();
};

const itemDiscountPercent = item => {
  const tablePrice = item.price;
  const selected = item.customPrice ?? tablePrice;
  if (!tablePrice || tablePrice === selected) return 0;
  const exactPercent = ((tablePrice - selected) / tablePrice) * 100;
  for (let decimals = 0; decimals <= 2; decimals += 1) {
    const factor = 10 ** decimals;
    const roundedPercent = Math.round(exactPercent * factor) / factor;
    const testPrice =
      Math.round(tablePrice * (1 - roundedPercent / 100) * 100) / 100;
    if (testPrice === selected) return roundedPercent;
  }
  return Math.round(exactPercent * 100) / 100;
};

const applyItemDiscountPercent = (item, percent) => {
  const upperBound =
    props.maxDiscountPercent != null
      ? Math.min(99.99, props.maxDiscountPercent)
      : 99.99;
  const clamped = Math.min(upperBound, Math.max(0, percent));
  const newPrice = Math.round(item.price * (1 - clamped / 100) * 100) / 100;
  emit('update-item-price', item.id, newPrice);
};

const handleItemDiscountInput = (item, value) => {
  const parsed = parseFloat(String(value).replace(',', '.'));
  if (!Number.isNaN(parsed)) applyItemDiscountPercent(item, parsed);
};

const incrementItemDiscount = item =>
  applyItemDiscountPercent(item, itemDiscountPercent(item) + 1);

const decrementItemDiscount = item =>
  applyItemDiscountPercent(item, itemDiscountPercent(item) - 1);

const selectItemPrice = (item, newPrice) =>
  emit('update-item-price', item.id, Number(newPrice));

onMounted(async () => {
  try {
    const { data } = await AtecPaymentMethodsAPI.fetch();
    paymentMethods.value = Array.isArray(data) ? data : [];
    if (paymentMethods.value.length > 0) selectPaymentMethod(paymentMethods.value[0]);
  } catch {
    // silent
  }

  try {
    const { data } = await AtecSalesAPI.index({
      conversationId: props.conversationId,
    });
    existingSales.value = Array.isArray(data) ? data : [];
  } catch {
    // silent
  }
});

const sendQuote = async () => {
  if (props.cart.length === 0) {
    useAlert('Carrinho vazio! Adicione produtos antes de enviar o orçamento.');
    return;
  }
  isSendingQuote.value = true;
  try {
    const totalOriginalPrice = props.cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    const totalSavings = totalOriginalPrice - total.value;

    let quoteMessage = '';
    props.cart.forEach(item => {
      const itemPrice = item.customPrice ?? item.price;
      const originalItemTotal = item.price * item.quantity;
      const finalItemTotal = itemPrice * item.quantity;
      quoteMessage += `${item.quantity} X ${item.name}\n`;
      if (originalItemTotal !== finalItemTotal) {
        quoteMessage += `De: ${formatPrice(originalItemTotal)}   Por: ${formatPrice(finalItemTotal)}\n`;
      } else {
        quoteMessage += `Por: ${formatPrice(finalItemTotal)}\n`;
      }
      quoteMessage += '\n';
    });

    quoteMessage += '_____________________________________________\n';
    quoteMessage += '*Total:*\n';
    if (totalSavings > 0) {
      quoteMessage += `De: ${formatPrice(totalOriginalPrice)}   Por: ${formatPrice(total.value)}\n`;
      quoteMessage += `Você economizou: ${formatPrice(totalSavings)}\n`;
    } else {
      quoteMessage += `${formatPrice(total.value)}\n`;
    }
    quoteMessage += '_____________________________________________\n';
    if (notes.value) quoteMessage += `\n*Observações:*\n${notes.value}\n`;

    const draftKey = `draft-${props.conversationId}-REPLY`;
    await store.dispatch('draftMessages/set', { key: draftKey, message: quoteMessage });
    emitter.emit('load-draft-message', {
      conversationId: props.conversationId,
      replyType: 'REPLY',
    });
    useAlert('Orçamento adicionado ao campo de mensagem. Revise e envie.');
  } catch {
    useAlert('Erro ao preparar orçamento. Tente novamente.');
  } finally {
    isSendingQuote.value = false;
  }
};

const finalizeSale = async () => {
  if (props.cart.length === 0) {
    useAlert('Carrinho vazio!');
    return;
  }
  if (!selectedPaymentMethodId.value) {
    useAlert('Selecione uma forma de pagamento.');
    return;
  }

  isProcessing.value = true;
  try {
    const today = new Date().toISOString().split('T')[0];
    const salePayload = {
      codigo: props.conversationId,
      codigo_cliente: 0,
      data: today,
      observacao: notes.value || '',
      codigo_formapgto: selectedPaymentMethodId.value,
      valor_total: subtotal.value,
      valor_desconto: discountAmount.value,
      valor_liquido: total.value,
      itens: props.cart.map((item, index) => ({
        item: index + 1,
        codigo_produto: item.id,
        quantidade: item.quantity,
        preco_unitario: Number(
          props.allowProductDiscountPerItem
            ? (item.customPrice ?? item.price)
            : item.price
        ),
        valor_desconto: props.allowProductDiscountPerItem
          ? Math.max(0, Number(item.price ?? 0) - Number(item.customPrice ?? item.price ?? 0)) * item.quantity
          : 0,
        valor_total:
          item.quantity *
          Number(
            props.allowProductDiscountPerItem
              ? (item.customPrice ?? item.price)
              : item.price
          ),
      })),
      pagamentos: [
        {
          codFormaPgto: selectedPaymentMethodId.value,
          descricao: selectedPaymentMethodName.value,
          valor: total.value,
          valorPago: total.value,
          valorTroco: 0,
          vcto: today,
          contaDB: '',
        },
      ],
    };

    await AtecSalesAPI.create({
      payload: salePayload,
      conversationId: props.conversationId,
      contactId: props.contactId,
    });

    useAlert('Venda finalizada com sucesso!');
    emit('clear-cart');
    notes.value = '';
    discount.value = 0;
    selectedPaymentMethodId.value = null;
    selectedPaymentMethodName.value = '';
    existingSales.value = [
      {
        sale_code: String(props.conversationId),
        sale_date: today,
        total_amount: total.value,
        created_at: new Date().toISOString(),
      },
      ...existingSales.value,
    ];
    allowResubmit.value = false;
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      'Erro ao finalizar venda. Tente novamente.';
    useAlert(msg);
  } finally {
    isProcessing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      v-if="cart.length === 0"
      class="flex flex-col items-center justify-center h-full text-center py-8"
    >
      <span class="text-4xl mb-2">🛒</span>
      <p class="text-n-text-subtle">
        {{ $t('PDV.EMPTY_CART') }}
      </p>
    </div>

    <div v-else class="relative flex flex-col lg:flex-row h-full">
      <!-- Banner: venda já enviada -->
      <div
        v-if="hasSentSale"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-n-background/95 p-6 text-center gap-4"
      >
        <span class="i-ph-check-circle-bold text-5xl text-green-500" />
        <h3 class="text-lg font-bold text-n-text">Venda já enviada</h3>
        <p class="text-sm text-n-text-subtle">
          Esta conversa já possui uma venda registrada.
        </p>
        <div
          class="bg-n-solid-2 border border-n-weak rounded-lg p-4 text-left w-full max-w-xs space-y-1"
        >
          <p class="text-xs text-n-text-subtle">
            Código:
            <span class="font-mono text-n-text">{{ latestSale.sale_code }}</span>
          </p>
          <p class="text-xs text-n-text-subtle">
            Enviada em:
            <span class="text-n-text">{{ formatDate(latestSale.created_at) }}</span>
          </p>
          <p class="text-xs text-n-text-subtle">
            Total:
            <span class="font-semibold text-n-brand">{{
              formatPrice(latestSale.total_amount)
            }}</span>
          </p>
        </div>
        <Button ghost slate xs @click="allowResubmit = true">
          Enviar nova venda mesmo assim
        </Button>
      </div>

      <!-- Itens do carrinho -->
      <div class="flex-1 min-w-0 overflow-y-auto p-4 space-y-3">
        <div class="space-y-3">
          <div
            v-for="item in cart"
            :key="item.id"
            class="bg-n-solid-1 rounded-lg p-3 border border-n-weak"
          >
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1 min-w-0">
                <h4 class="font-semibold text-n-text text-sm truncate">
                  {{ item.name }}
                </h4>
                <p class="text-xs text-n-text-subtle">
                  Tabela: {{ formatPrice(item.price) }} / un
                </p>
              </div>
              <Button
                ghost
                danger
                xs
                icon="i-ph-trash-bold"
                @click="removeItem(item.id)"
              />
            </div>

            <!-- Preço e Desconto por item -->
            <div class="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label class="text-xs text-n-text-subtle mb-1 block">Preço</label>
                <div
                  v-if="item.availablePrices && item.availablePrices.length > 1"
                  class="relative"
                >
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-2 py-1 rounded border border-n-weak bg-n-background text-n-text text-sm text-left"
                    @click="togglePriceDropdown(item.id)"
                  >
                    <span>
                      {{
                        item.availablePrices.find(
                          o => o.price === (item.customPrice ?? item.price)
                        )?.label ?? ''
                      }}
                      – {{ formatPrice(item.customPrice ?? item.price) }}
                    </span>
                    <span
                      class="i-lucide-chevron-down ml-1 shrink-0 transition-transform"
                      :class="{ 'rotate-180': priceDropdownOpenId === item.id }"
                    />
                  </button>
                  <div
                    v-if="priceDropdownOpenId === item.id"
                    class="mt-1 rounded border border-n-weak bg-n-solid-2"
                  >
                    <button
                      v-for="opt in item.availablePrices"
                      :key="opt.price"
                      type="button"
                      class="w-full flex items-center justify-between px-2 py-1 text-sm text-left hover:bg-n-slate-3"
                      @click="selectItemPrice(item, opt.price); closePriceDropdown()"
                    >
                      <span>{{ opt.label }} – {{ formatPrice(opt.price) }}</span>
                      <span
                        v-if="opt.price === (item.customPrice ?? item.price)"
                        class="i-lucide-check ml-1 shrink-0 text-n-brand"
                      />
                    </button>
                  </div>
                </div>
                <span
                  v-else-if="props.allowProductDiscountPerItem"
                  class="text-sm font-medium"
                  :class="
                    itemDiscountPercent(item) > 0 ? 'text-green-600' : 'text-n-text'
                  "
                >
                  {{ formatPrice(item.customPrice ?? item.price) }}
                </span>
                <span v-else class="text-sm font-medium text-n-text">
                  {{ formatPrice(item.price) }}
                </span>
              </div>
              <div>
                <label class="text-xs text-n-text-subtle mb-1 block">Desconto</label>
                <span v-if="!props.allowProductDiscountPerItem" class="text-sm text-n-text-subtle">—</span>
                <div v-else class="flex items-center gap-1 -ml-1">
                  <Button ghost xs icon="i-ph-minus-bold" @click="decrementItemDiscount(item)" />
                  <div
                    class="flex items-center justify-center w-16 border rounded overflow-hidden"
                    :class="itemDiscountPercent(item) > 0 ? 'border-green-400' : 'border-n-weak'"
                  >
                    <input
                      type="text"
                      inputmode="decimal"
                      :value="itemDiscountPercent(item)"
                      class="w-full text-sm font-semibold text-right bg-transparent !border-none !shadow-none !outline-none p-0 h-7 focus:ring-0 min-w-0"
                      :class="itemDiscountPercent(item) > 0 ? 'text-green-600' : 'text-n-text'"
                      style="border: none !important; box-shadow: none !important; background: transparent !important; padding-right: 2px !important; margin: 0 !important;"
                      @change="e => handleItemDiscountInput(item, e.target.value)"
                    />
                    <span
                      class="text-sm font-semibold pr-1.5"
                      :class="itemDiscountPercent(item) > 0 ? 'text-green-600' : 'text-n-text-subtle'"
                      >%</span
                    >
                  </div>
                  <Button ghost xs icon="i-ph-plus-bold" @click="incrementItemDiscount(item)" />
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <Button
                  ghost
                  xs
                  icon="i-ph-minus-bold"
                  @click="updateItemQuantity(item.id, item.quantity - 1)"
                />
                <span class="text-sm font-medium px-2">{{ item.quantity }}</span>
                <Button
                  ghost
                  xs
                  icon="i-ph-plus-bold"
                  @click="updateItemQuantity(item.id, item.quantity + 1)"
                />
              </div>
              <div class="text-right">
                <span
                  v-if="item.customPrice != null && item.customPrice !== item.price"
                  class="block text-xs text-n-text-subtle line-through"
                >
                  {{ formatPrice(item.price * item.quantity) }}
                </span>
                <span class="font-bold text-n-brand">
                  {{ formatPrice((item.customPrice ?? item.price) * item.quantity) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Painel lateral: resumo e finalização -->
      <div
        class="w-full lg:w-[360px] xl:w-[420px] border-t border-n-weak lg:border-t-0 lg:border-l p-4 space-y-4 bg-n-solid-1 overflow-y-auto"
      >
        <!-- Desconto global -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-text">
            {{ $t('PDV.DISCOUNT') }} (%)
          </label>
          <input
            v-model.number="discount"
            type="number"
            min="0"
            max="100"
            class="w-full px-3 py-2 rounded border border-n-weak bg-n-background text-n-text text-sm"
            placeholder="0"
          />
        </div>

        <!-- Forma de Pagamento -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-text">
            {{ $t('PDV.PAYMENT_METHOD') }}
          </label>
          <div v-if="paymentMethods.length > 0">
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 rounded border border-n-weak bg-n-background text-n-text text-left"
              @click="paymentDropdownOpen = !paymentDropdownOpen"
            >
              <span>{{ selectedPaymentMethodName || 'Selecione...' }}</span>
              <span
                class="i-lucide-chevron-down ml-1 shrink-0 transition-transform"
                :class="{ 'rotate-180': paymentDropdownOpen }"
              />
            </button>
            <div
              v-if="paymentDropdownOpen"
              class="mt-1 rounded border border-n-weak bg-n-solid-2 max-h-48 overflow-y-auto"
            >
              <button
                v-for="method in paymentMethods"
                :key="method.Fpg_Codigo ?? method.codigo ?? method.id"
                type="button"
                class="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-n-slate-3"
                @click="selectPaymentMethod(method)"
              >
                <span>{{ method.Fpg_Descricao ?? method.descricao ?? method.nome }}</span>
                <span
                  v-if="selectedPaymentMethodId === (method.Fpg_Codigo ?? method.codigo ?? method.id)"
                  class="i-lucide-check ml-1 shrink-0 text-n-brand"
                />
              </button>
            </div>
          </div>
          <p v-else class="text-sm text-n-text-subtle">
            Nenhuma forma de pagamento carregada.
          </p>
        </div>

        <!-- Observações -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-text">
            {{ $t('PDV.NOTES') }}
          </label>
          <textarea
            v-model="notes"
            :placeholder="$t('PDV.NOTES_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded border border-n-weak bg-n-background text-n-text resize-none text-sm"
            rows="2"
          />
        </div>

        <!-- Totais -->
        <div class="space-y-2 pt-4 border-t border-n-weak">
          <div class="flex justify-between text-sm">
            <span class="text-n-text-subtle">{{ $t('PDV.SUBTOTAL') }}</span>
            <span class="text-n-text">{{ formatPrice(subtotal) }}</span>
          </div>
          <div v-if="discount > 0" class="flex justify-between text-sm">
            <span class="text-n-text-subtle">
              {{ $t('PDV.DISCOUNT') }} ({{ discount }}%)
            </span>
            <span class="text-green-600">-{{ formatPrice(discountAmount) }}</span>
          </div>
          <div
            class="flex justify-between text-lg font-bold pt-2 border-t border-n-weak"
          >
            <span class="text-n-text">{{ $t('PDV.TOTAL') }}</span>
            <span class="text-n-brand">{{ formatPrice(total) }}</span>
          </div>
        </div>

        <!-- Botões de Ação -->
        <div class="space-y-2">
          <Button
            class="w-full"
            variant="outline"
            slate
            :loading="isSendingQuote"
            :disabled="isProcessing"
            @click="sendQuote"
          >
            <span class="i-ph-file-text-bold mr-2" />
            {{ $t('PDV.SEND_QUOTE') }}
          </Button>
          <Button
            class="w-full"
            :loading="isProcessing"
            :disabled="isSendingQuote"
            @click="finalizeSale"
          >
            <span class="i-ph-check-circle-bold mr-2" />
            {{ $t('PDV.FINALIZE_SALE') }}
          </Button>
          <Button
            class="w-full"
            ghost
            danger
            :disabled="isProcessing || isSendingQuote"
            @click="clearCart"
          >
            <span class="i-ph-trash-bold mr-2" />
            {{ $t('PDV.CLEAR_CART') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
